import type { QueryEntity, QueryFilter, QueryFieldType } from '@app-types';

/**
 * Validate filter values against their field types, mirroring the backend
 * filter-validator so the user gets immediate feedback instead of an empty result
 * set or a swallowed 400 (bugs 868/887/888/891/944). Shared by the query builder
 * (Run/Save) and the results page (Apply filters).
 * Returns the first error message, or null when every filter is valid.
 */
const fieldLabel = (schemaEntities: QueryEntity[], entity: string, field: string): string =>
  schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.label ?? field;

const fieldType = (schemaEntities: QueryEntity[], entity: string, field: string): QueryFieldType =>
  schemaEntities.find((e) => e.key === entity)?.fields.find((f) => f.key === field)?.type ?? 'text';

/** Validates a single "dateRange" filter; returns an error message, or null when valid. */
const validateDateRangeFilter = (filter: QueryFilter, label: string): string | null => {
  if (!filter.value || !filter.valueTo) return null;
  const from = new Date(String(filter.value)).getTime();
  const to = new Date(String(filter.valueTo)).getTime();
  return from > to ? `Filter "${label}": the From date must be before the To date.` : null;
};

/** Validates a single non-dateRange filter; returns an error message, or null when valid. */
const validateValueFilter = (filter: QueryFilter, type: QueryFieldType, label: string): string | null => {
  const missing = filter.value === undefined || filter.value === null || filter.value === '';
  if (missing) return `Filter "${label}": please enter a value.`;
  if (type === 'number' && !Number.isFinite(Number(filter.value))) {
    return `Filter "${label}": please enter a valid number.`;
  }
  return null;
};

export const validateFilters = (
  filters: QueryFilter[],
  schemaEntities: QueryEntity[]
): string | null => {
  for (const filter of filters) {
    if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') continue;
    const label = fieldLabel(schemaEntities, filter.entity, filter.field);

    const error =
      filter.operator === 'dateRange'
        ? validateDateRangeFilter(filter, label)
        : validateValueFilter(filter, fieldType(schemaEntities, filter.entity, filter.field), label);

    if (error) return error;
  }
  return null;
};
